import React, { useCallback, useEffect, useState } from 'react';
import { Select, Alert } from 'antd';
import dayjs from 'dayjs';
import { fetchOrderEarnings } from '../../api/webshop-products-orders-api';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import GenericTable from '../tables/GenericTable';
import Spinner from '../elements/Spinner/Spinner';

const MONTH_LABELS = [
  'Januar',
  'Februar',
  'Mart',
  'April',
  'Maj',
  'Jun',
  'Jul',
  'Avgust',
  'Septembar',
  'Oktobar',
  'Novembar',
  'Decembar',
];

// The webshop has no orders before this, so there is nothing to look at
// further back.
const FIRST_YEAR = 2024;

const yearOptions = () => {
  const currentYear = dayjs().year();
  const years = [];
  for (let year = currentYear; year >= FIRST_YEAR; year -= 1) {
    years.push({ value: year, label: String(year) });
  }
  return years;
};

const summaryCardStyle = {
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: '20px 24px',
  minWidth: 220,
};

export default function OrderEarnings() {
  const [year, setYear] = useState(dayjs().year());
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadEarnings = useCallback(async () => {
    setLoading(true);
    const { data, error: loadError } = await fetchOrderEarnings({ year });
    if (loadError) {
      setError(loadError);
      setEarnings(null);
    } else {
      setError('');
      setEarnings(data);
    }
    setLoading(false);
  }, [year]);

  useEffect(() => {
    loadEarnings();
  }, [loadEarnings]);

  const columns = [
    {
      key: 'month',
      label: 'Mesec',
      render: (val) => MONTH_LABELS[val - 1] || val,
    },
    { key: 'orders', label: 'Broj narudžbina' },
    {
      key: 'amount',
      label: 'Zarada',
      render: (val) => formatPriceWithCurrency(val || 0),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          marginTop: 12,
          marginBottom: 20,
        }}
      >
        <p className="title" style={{ margin: 0 }}>
          Zarada po mesecima
        </p>
        <Select
          value={year}
          options={yearOptions()}
          onChange={setYear}
          style={{ minWidth: 120 }}
        />
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <Spinner size="lg" />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 24,
            }}
          >
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Ukupna zarada u {year}.
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {formatPriceWithCurrency(earnings?.total?.amount || 0)}
              </div>
            </div>
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Broj narudžbina
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {earnings?.total?.orders || 0}
              </div>
            </div>
          </div>

          <div className="products_wrapper">
            <GenericTable
              data={earnings?.months || []}
              columns={columns}
              emptyState={{
                icon: '/assets/icons/no-ads.svg',
                title: 'Nema podataka o zaradi',
              }}
            />
          </div>

          <p style={{ color: '#6B7280', fontSize: 13, marginTop: 16 }}>
            Obračunate su samo naplaćene narudžbine — plaćene karticom, ili
            pouzećem / lično uz status „Isporučeno“. Otkazane i vraćene
            narudžbine, kao i kupovine poenima, nisu uračunate.
          </p>
        </>
      )}
    </div>
  );
}
